import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import type React from "react";
import type { ComponentProps } from "react";
import { useState } from "react";
import { toast } from "react-toastify";
import { InvoiceItemAddForm } from "~/components/common/constructions/adding/InvoiceItemAddForm";
import { ChangeAmountModal } from "~/components/common/constructions/change_amount/ChangeAmountModal";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { SettlementList } from "~/components/common/settlements/list/SettlementList";
import { useDealBillings } from "~/hooks/deals/useDealBillings";
import { useDealEstimationHistories } from "~/hooks/deals/useDealEstimationHistories";
import { useDealConstructionItems } from "~/hooks/useDealConstructionItems";
import { useDeals } from "~/hooks/useDeals";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useUsingApplications } from "~/hooks/useUsingApplications";
import type { NskResponse } from "~/models/Api";
import type {
	DealConstruction,
	DealConstructionEdit
} from "~/models/DealConstruction";
import type {
	DealSettlement,
	DealSettlementEdit
} from "~/models/DealSettlement";
import { isOwnStatus } from "~/models/Status";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import {
	destroy as destroyBilling,
	create as postDealBilling,
	update as updateDealBilling,
	updateSeq
} from "~/utils/api/deals/DealBilling";
import { getDownloadPath } from "~/utils/api/deals/DealBillingFile";
import { create as generateBillings } from "~/utils/api/deals/DealBillingGenerate";
import { updateAccept } from "~/utils/api/deals/DealEstimationHeader";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const [showChangeAmountModal, setShowChangeAmountModal] = useState(false);
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const { data: deal } = useDeals().show(id);
	const { data: dealConstructionItems } = useDealConstructionItems();
	const { data: dealEstimationHistoryContext } =
		useDealEstimationHistories().index(id);
	const { useSK } = useUsingApplications();
	const { data: billingContext, mutate: mutateBillings } =
		useDealBillings().index(id, "rentee");
	if (
		deal === null ||
		billingContext === null ||
		dealConstructionItems === null ||
		dealEstimationHistoryContext === null
	) {
		return <Loading isLoading />;
	}
	const estimationHeaderId =
		dealEstimationHistoryContext.dealEstimationHeaderId;
	const pageTitle = useSK
		? "入居者への請求書作成"
		: "入居者への請求書・精算書作成";
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: pageTitle }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];
	const handleClickPreview = (): void => {
		const path = getDownloadPath(id, "deal_billing_rentee");
		window.open(path);
	};
	const handleMutateConstructions = async (): Promise<void> => {
		await mutateBillings?.();
	};
	const handleCreate = async (
		submitValue: DealConstructionEdit | DealSettlementEdit
	): Promise<NskResponse> => postDealBilling(submitValue, deal.id, "rentee");
	const handleUpdate = async (
		submitValue: DealConstructionEdit | DealSettlementEdit,
		billingId: number | null
	): Promise<NskResponse> => {
		if (billingId === null) {
			throw new Error("billingId cannot be null");
		}
		return updateDealBilling(submitValue, billingId, deal.id);
	};
	const handleDestroy = async (
		deleteBilling: DealConstruction | DealSettlement
	): Promise<NskResponse> => {
		if (deleteBilling.type !== "DealBilling") {
			throw new Error(`Illegal construction type: ${deleteBilling.type}`);
		}
		if (deleteBilling.billingType !== "rentee") {
			throw new Error(`Illegal billing type: ${deleteBilling.billingType}`);
		}
		return destroyBilling(deleteBilling, deal.id);
	};
	const handleUpdateSeq = async (
		newBillings: DealConstruction[] | DealSettlement[]
	): Promise<void> => {
		await updateSeq(newBillings, deal.id);
	};
	const handleClickGenerateBillings = async (): Promise<void> => {
		// 非共有案件で申請前(draftのheader)が存在する場合は承認させる
		// https://itandi-dev.atlassian.net/browse/NSKDEV-1622対応
		if (isOwnStatus(deal.statusId) && estimationHeaderId !== null) {
			const response = await updateAccept(deal.id, estimationHeaderId);
			if (!isSuccess(response)) {
				toast.error("エラーが発生しました");
				handleClose();
				return;
			}
		}
		const result = await generateBillings(deal.id, "rentee");
		if (isSuccess(result)) {
			await mutateBillings?.();
			toast.success("請求を再作成しました");
			handleClose();
			return;
		}
		toast.error("エラーが発生しました");
		handleClose();
	};

	return (
		<DefaultV2>
			<ClientDealLayout deal={deal} items={items}>
				<h2 className={Styles.EstimateTitle}>{pageTitle}</h2>
				<ConstructionTop
					billingTarget="rentee"
					constructionContext={billingContext}
					handleClickBillingPreview={handleClickPreview}
					handleClickChangeAmount={(): void => {
						setShowChangeAmountModal(true);
					}}
					handleClickGenerateBillings={handleOpen}
					readonly={false}
					useSK={useSK}
				/>
				{showChangeAmountModal ? (
					<ChangeAmountModal
						billingContext={billingContext}
						billingType="rentee"
						onClose={(): void => {
							setShowChangeAmountModal(false);
						}}
					/>
				) : null}
				{isOpen ? (
					<ConfirmModal
						confirmType="save"
						handleAccept={handleClickGenerateBillings}
						handleCancel={handleClose}
						isOpen={isOpen}
					>
						工事見積りから請求を再作成しますか？
						<Alert marginTop={8}>
							現在登録されている入居者への請求情報は削除されます
						</Alert>
					</ConfirmModal>
				) : null}
				<div className={Styles.Container}>
					<div className={Styles.EstimateAddForm}>
						<InvoiceItemAddForm
							billingType="rentee"
							handleCreate={handleCreate}
							mutateConstructions={handleMutateConstructions}
						/>
					</div>
					<div className={Styles.EstimateList}>
						{billingContext.dealSettlements.length > 0 && (
							<SettlementList
								constructionContext={billingContext}
								handleDestroy={handleDestroy}
								handleMutateSettlements={handleMutateConstructions}
								handleUpdate={handleUpdate}
								handleUpdateSeq={handleUpdateSeq}
								target="rentee"
							/>
						)}
						<ConstructionList
							companyType="client"
							constructionContext={billingContext}
							deal={deal}
							dealConstructionItems={dealConstructionItems}
							docType="billing"
							handleDestroy={handleDestroy}
							handleMutateConstructions={handleMutateConstructions}
							handleUpdate={handleUpdate}
							handleUpdateSeq={handleUpdateSeq}
							readonly={false}
							target="rentee"
						/>
					</div>
				</div>
			</ClientDealLayout>
		</DefaultV2>
	);
};
